import { PublicClientApplication } from '@azure/msal-browser'
import type { AccountInfo } from '@azure/msal-browser'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { acquireToken, configFromEnvironment, msalConfiguration } from './msal'
import type { AuthConfig } from './msal'

/** The three states `04 §4.3` designs for, and one for "still finding out". */
export type AuthState = 'loading' | 'signed-out' | 'signed-in' | 'not-entitled'

interface AuthContextValue {
  state: AuthState
  account: AccountInfo | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  token: () => Promise<string>

  /**
   * True while a silent refresh is failing. THE PAGE KEEPS EVERYTHING IT HAS — this drives an
   * indicator, not an unmount.
   */
  stale: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth outside AuthProvider')
  return value
}

/**
 * THE CONFIGURATION IS READ ONCE, AT MODULE LOAD.
 *
 * <p>It was a DEFAULT PARAMETER — `config = configFromEnvironment()` — which JavaScript evaluates
 * on EVERY RENDER, producing a new object each time. That identity change cascaded:
 * `useMemo(msal, [config])` built a new `PublicClientApplication`, `useCallback(token, [...])` got a
 * new identity, and every effect keyed on `token` tore itself down and restarted.</p>
 *
 * <p>The browser showed it as an abort storm — the workspace's list stream opened and aborted more
 * than 150 times in under a minute, each one a real request to a real Function App. Nothing in the
 * console said anything was wrong, because nothing was failing: the effect was doing exactly what
 * it had been told, which was to restart whenever its dependency changed.</p>
 */
const ENVIRONMENT_CONFIG = configFromEnvironment()

export function AuthProvider({ children, config = ENVIRONMENT_CONFIG, groupObjectId = '' }:
  { children: ReactNode; config?: AuthConfig; groupObjectId?: string }): React.JSX.Element {
  const msal = useMemo(() => new PublicClientApplication(msalConfiguration(config)), [config])
  const [state, setState] = useState<AuthState>('loading')
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [stale, setStale] = useState(false)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      await msal.initialize()
      const redirect = await msal.handleRedirectPromise()
      if (cancelled) return

      const active = redirect?.account ?? msal.getAllAccounts()[0] ?? null
      setAccount(active)
      setState(active === null ? 'signed-out'
        : entitled(active, groupObjectId) ? 'signed-in' : 'not-entitled')
    })()

    return () => { cancelled = true }
  }, [msal, groupObjectId])

  const signIn = useCallback(async () => {
    // REDIRECT, AND THE DEEP LINK SURVIVES IT. `navigateToLoginRequestUrl` brings the user back to
    // the gate they followed a notification to, rather than to the workspace list.
    await msal.loginRedirect({ scopes: [config.scope] })
  }, [msal, config.scope])

  const signOut = useCallback(async () => {
    await msal.logoutRedirect({ account })
  }, [msal, account])

  const token = useCallback(async () => {
    if (!account) throw new Error('not signed in')

    try {
      const value = await acquireToken(msal, account, config.scope)
      setStale(false)
      return value
    } catch (error) {
      // THE SIGNED-OUT INDICATOR, AND NOTHING IS DISCARDED. The user re-authenticates in place,
      // without a browser refresh — everything already received stays on the page.
      setStale(true)
      throw error
    }
  }, [msal, account, config.scope])

  const value = useMemo<AuthContextValue>(
    () => ({ state, account, signIn, signOut, token, stale }),
    [state, account, signIn, signOut, token, stale])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function entitled(account: AccountInfo, groupObjectId: string): boolean {
  if (groupObjectId.length === 0) return true
  const groups = (account.idTokenClaims as { groups?: unknown } | undefined)?.groups
  return Array.isArray(groups) && groups.includes(groupObjectId)
}
